"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react-safe";

import type { SessionUser } from "@/lib/auth";

export type SessionStatus = "loading" | "authenticated" | "unauthenticated";

export type ClientSession = {
  user: SessionUser;
  issuedAt: number;
  expiresAt: number;
  passwordNeedsChange?: boolean;
};

export type CredentialSignInParams = {
  email: string;
  password: string;
  redirect?: boolean;
  callbackUrl?: string;
};

export type TwoFactorChallengeClient = {
  token: string;
  methods: string[];
  issuedAt: number;
  expiresAt: number;
  config: {
    digits: number;
    period: number;
    issuer: string;
    label: string;
  };
};

export type SignInResult = {
  ok?: true;
  error?: string;
  session?: ClientSession;
  twoFactorRequired?: true;
  challenge?: TwoFactorChallengeClient;
};

export type SignOutOptions = {
  redirect?: boolean;
  callbackUrl?: string;
};

type FetchSessionOptions = {
  checkPasswordChange?: boolean;
};

type AuthContextValue = {
  session: ClientSession | null;
  status: SessionStatus;
  refresh: (options?: FetchSessionOptions) => Promise<ClientSession | null>;
  setSession: (session: ClientSession | null, status?: SessionStatus) => void;
  signIn: (params: CredentialSignInParams) => Promise<SignInResult>;
  signOut: (options?: SignOutOptions) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

let clientApi: AuthContextValue | undefined;

function setClientApi(api: AuthContextValue | undefined) {
  clientApi = api;
}

async function fetchSessionFromApi(options?: FetchSessionOptions): Promise<ClientSession | null> {
  const query = options?.checkPasswordChange ? "?checkPasswordChange=true" : "";

  try {
    const res = await fetch(`/api/auth/session${query}`, {
      credentials: "include",
    });

    if (!res.ok) {
      return null;
    }

    const data = await res.json();
    if (!data?.authenticated || !data?.session) {
      return null;
    }

    return {
      user: data.session.user,
      issuedAt: data.session.issuedAt,
      expiresAt: data.session.expiresAt,
      passwordNeedsChange: data.passwordNeedsChange ?? false,
    } satisfies ClientSession;
  } catch (error) {
    return null;
  }
}

function normalizeRedirect(url?: string, fallback = "/modules") {
  if (!url) return fallback;
  try {
    const nextUrl = new URL(url, window.location.origin);
    return `${nextUrl.pathname}${nextUrl.search}`;
  } catch {
    return fallback;
  }
}

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
  const [session, setSessionState] = useState<ClientSession | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  const refreshPromiseRef = useRef<Promise<ClientSession | null> | null>(null);

  const refresh = useCallback(async (options?: FetchSessionOptions) => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = (async () => {
        const next = await fetchSessionFromApi(options);
        if (next) {
          setSessionState(next);
          setStatus("authenticated");
        } else {
          setSessionState(null);
          setStatus("unauthenticated");
        }
        refreshPromiseRef.current = null;
        return next;
      })();
    }

    return refreshPromiseRef.current;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setSession = useCallback((next: ClientSession | null, nextStatus?: SessionStatus) => {
    setSessionState(next);
    if (nextStatus) {
      setStatus(nextStatus);
    } else {
      setStatus(next ? "authenticated" : "unauthenticated");
    }
  }, []);

  const signIn = useCallback(
    async ({ email, password, redirect = true, callbackUrl }: CredentialSignInParams): Promise<SignInResult> => {
      setStatus("loading");

      try {
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });

        const data = await res.json().catch(() => null);

        if (res.status === 202 || data?.twoFactorRequired) {
          setSession(null, "unauthenticated");
          if (data?.challenge?.token) {
            return {
              twoFactorRequired: true,
              challenge: data.challenge,
            };
          }
          return { error: "Two-factor authentication required" };
        }

        if (!res.ok) {
          const error = res.status === 401 ? "Invalid email or password" : "Login failed";
          await refresh();
          return { error };
        }

        if (!data?.session?.user) {
          await refresh();
          return { error: "Session not returned" };
        }

        const nextSession: ClientSession = {
          user: data.session.user,
          issuedAt: data.session.issuedAt,
          expiresAt: data.session.expiresAt,
          passwordNeedsChange: data.passwordNeedsChange ?? false,
        };

        setSession(nextSession, "authenticated");

        if (redirect) {
          window.location.href = normalizeRedirect(callbackUrl);
        }

        return { ok: true, session: nextSession };
      } catch (error) {
        await refresh();
        return { error: (error as Error)?.message ?? "Login failed" };
      }
    },
    [refresh, setSession],
  );

  const signOut = useCallback(
    async ({ redirect = true, callbackUrl }: SignOutOptions = {}) => {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // Ignore network errors on logout
      }

      setSession(null, "unauthenticated");

      if (redirect) {
        window.location.href = normalizeRedirect(callbackUrl, "/login");
      }
    },
    [setSession],
  );

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      session,
      status,
      refresh,
      setSession,
      signIn,
      signOut,
    }),
    [session, status, refresh, setSession, signIn, signOut],
  );

  useEffect(() => {
    setClientApi(contextValue);
    return () => {
      if (clientApi === contextValue) {
        setClientApi(undefined);
      }
    };
  }, [contextValue]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthContextProvider");
  }
  return ctx;
}

export function useSession() {
  const ctx = useAuthContext();
  return {
    data: ctx.session,
    status: ctx.status,
    refresh: ctx.refresh,
    signIn: ctx.signIn,
    signOut: ctx.signOut,
  };
}

export async function signIn(provider: string, options?: CredentialSignInParams): Promise<SignInResult> {
  if (provider !== "credentials") {
    throw new Error(`Unsupported provider: ${provider}`);
  }

  if (!options) {
    throw new Error("Missing credentials for signIn");
  }

  if (!clientApi) {
    throw new Error("AuthProvider is not mounted");
  }

  return clientApi.signIn(options);
}

export async function signOut(options?: SignOutOptions) {
  if (!clientApi) {
    throw new Error("AuthProvider is not mounted");
  }

  return clientApi.signOut(options);
}

export async function getSession(options?: FetchSessionOptions): Promise<ClientSession | null> {
  if (clientApi?.session && !options?.checkPasswordChange) {
    return clientApi.session;
  }

  return fetchSessionFromApi(options);
}

export async function refreshSession(options?: FetchSessionOptions) {
  if (!clientApi) {
    return fetchSessionFromApi(options);
  }

  return clientApi.refresh(options);
}
